import { ArrowUturnLeftIcon, CheckCircleIcon, EyeIcon, EyeSlashIcon, TrashIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, useMemo, useState } from 'react';
import { parseObjectDefinitionSpec, type ObjectDefinitionSpec } from 'shared-types';
import { ConfirmDialog } from '../../../components/Modal';
import { Tooltip } from '../../../components/Tooltip';
import { useFormatDate } from '../../../hooks/useFormatDate';
import { evaluateCustomGlyph } from '../../../potree/sketchObjects/custom/evaluateCustomGlyph';
import { T, useT } from '../../../translation/src';
import {
  useObjectDefinitionActivateVersionMutation,
  useObjectDefinitionDeleteVersionMutation,
  useObjectDefinitionRestoreVersionMutation,
  useObjectDefinitionVersionsQuery,
} from '../../../types/graphqlTypes';
import { GlyphPreview } from '../../objectLibrary/GlyphPreview';
import { useObjectEditor } from '../EditorContext';
import { PanelSection } from '../panels/fields';

interface VersionRow {
  version: number;
  spec: ObjectDefinitionSpec;
  glyph: number[];
  publishedAt: number;
  publishedByName: string | null;
  isCurrent: boolean;
}

type Pending = { action: 'restore' | 'activate' | 'delete'; row: VersionRow };

const defaultGlyph = (spec: ObjectDefinitionSpec): number[] =>
  evaluateCustomGlyph(spec, { center: { x: 0, y: 0 }, dir: { x: 1, y: 0 }, norm: { x: 0, y: 1 }, width: spec.defaultSize.width, depth: spec.defaultSize.depth, z: 0 }, {});

/** A small icon button with a tooltip (the version rows' actions). */
const RowAction: React.FC<{ label: string; icon: typeof EyeIcon; onClick: () => void; disabled?: boolean; danger?: boolean; active?: boolean }> = ({ label, icon: Icon, onClick, disabled, danger, active }) => (
  <Tooltip delay={0} message={<span>{label}</span>}>
    <button
      type="button"
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      disabled={disabled}
      className={classNames(
        'flex h-7 w-7 items-center justify-center rounded-md transition-colors disabled:cursor-not-allowed disabled:opacity-30',
        danger ? 'text-error-red-500 hover:bg-error-red-500/10' : 'text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-white/10',
        active && 'bg-gray-200 dark:bg-white/10',
      )}
    >
      <Icon className="h-4 w-4" />
    </button>
  </Tooltip>
);

/**
 * Every published version, newest first. A row click previews it read-only.
 * Per version: make it the active one (what projects render, no new
 * publish), restore it as the draft, or delete it (not the active one).
 */
const HistoryPanel_: React.FC = () => {
  const { organisationId, definition, ui, setUi, resetDraft } = useObjectEditor();
  const variables = { organisationId, definitionId: definition.id };
  const { data, loading } = useObjectDefinitionVersionsQuery({ variables, fetchPolicy: 'cache-and-network' });
  const refetchQueries = ['objectDefinitionVersions'];
  const [restore, { loading: restoring }] = useObjectDefinitionRestoreVersionMutation();
  const [activate, { loading: activating }] = useObjectDefinitionActivateVersionMutation({ refetchQueries });
  const [deleteVersion, { loading: deleting }] = useObjectDefinitionDeleteVersionMutation({ refetchQueries });
  const busy = restoring || activating || deleting;
  const [pending, setPending] = useState<Pending | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { formatDateAndTime } = useFormatDate();
  const failed = {
    restore: useT('the version could not be restored', { swc: true }),
    activate: useT('the version could not be made active', { swc: true }),
    delete: useT('the version could not be deleted', { swc: true }),
  };
  const labels = {
    preview: useT('preview', { swc: true }),
    stopPreview: useT('stop preview', { swc: true }),
    activate: useT('make active: projects use this version', { swc: true }),
    restore: useT('restore as draft', { swc: true }),
    delete: useT('delete version', { swc: true }),
    activeCannotDelete: useT('the active version cannot be deleted', { swc: true }),
  };
  const unknownPublisher = useT('before history was kept', { swc: true });

  const rows = useMemo<VersionRow[]>(
    () =>
      (data?.objectDefinitionVersions ?? []).flatMap((v) => {
        const parsed = parseObjectDefinitionSpec(v.spec);
        return 'spec' in parsed ? [{ version: v.version, spec: parsed.spec, glyph: defaultGlyph(parsed.spec), publishedAt: v.publishedAt, publishedByName: v.publishedByName ?? null, isCurrent: v.isCurrent }] : [];
      }),
    [data],
  );

  const togglePreview = (row: VersionRow) => setUi((u) => ({ ...u, mode: 'edit', preview: u.preview?.version === row.version ? null : { version: row.version, spec: row.spec } }));

  const run = async ({ action, row }: Pending) => {
    setError(null);
    const vars = { ...variables, version: row.version };
    try {
      if (action === 'delete') {
        await deleteVersion({ variables: vars });
        if (ui.preview?.version === row.version) setUi((u) => ({ ...u, preview: null }));
        return;
      }
      const result = action === 'restore' ? (await restore({ variables: vars })).data?.objectDefinitionRestoreVersion : (await activate({ variables: vars })).data?.objectDefinitionActivateVersion;
      // Restoring always replaces the draft; activating only when the draft had no edits of its own.
      if (action === 'restore' || !definition.hasUnpublishedChanges) {
        const parsed = parseObjectDefinitionSpec(result?.draftSpec);
        if ('spec' in parsed) resetDraft(parsed.spec);
      }
    } catch {
      setError(failed[action]);
    }
  };

  const confirmation = pending
    ? {
        restore: {
          variant: 'warning' as const,
          title: <T _str="replace the draft?" swc />,
          message: <T _str="the draft has unpublished changes; restoring this version replaces them. Projects keep the published version until you publish again." swc />,
          confirmLabel: <T _str="restore as draft" swc />,
        },
        activate: {
          variant: 'warning' as const,
          title: <T _str="make v{version} active?" args={{ version: pending.row.version }} swc />,
          message: definition.hasUnpublishedChanges ? (
            <T _str="every placed object of this type switches to this version. Your unpublished draft is kept." swc />
          ) : (
            <T _str="every placed object of this type switches to this version, and the draft follows it." swc />
          ),
          confirmLabel: <T _str="make active" swc />,
        },
        delete: {
          variant: 'danger' as const,
          title: <T _str="delete v{version}?" args={{ version: pending.row.version }} swc />,
          message: <T _str="the version is removed from the history. Placed objects are not affected." swc />,
          confirmLabel: <T _str="delete" swc />,
        },
      }[pending.action]
    : null;

  return (
    <PanelSection title={<T _str="published versions" />}>
      {error && (
        <p role="alert" className="rounded-md bg-error-red-500/10 px-2 py-1 text-xs text-error-red-500">
          {error}
        </p>
      )}
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">{loading ? '…' : <T _str="not published yet: each publish is kept here" swc />}</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {rows.map((row) => {
            const previewing = ui.preview?.version === row.version;
            return (
              <li key={row.version}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => togglePreview(row)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      togglePreview(row);
                    }
                  }}
                  className={classNames(
                    'group flex cursor-pointer items-center gap-2.5 rounded-lg border p-1.5 transition-colors',
                    previewing ? 'border-neon-green-500 bg-neon-green-300/10' : 'border-transparent hover:border-gray-300 hover:bg-gray-100 dark:hover:border-gray-600 dark:hover:bg-white/5',
                  )}
                >
                  <div className="h-11 w-11 shrink-0 rounded bg-white/60 p-1 text-gray-800 dark:bg-black/25 dark:text-gray-100">
                    <GlyphPreview segments={row.glyph} />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <div className="flex items-center gap-1.5 text-sm font-medium">
                      v{row.version}
                      {row.isCurrent && (
                        <span className="rounded-full bg-neon-green-300/50 px-1.5 text-[10px] leading-4 text-gray-900 dark:text-white">
                          <T _str="active" />
                        </span>
                      )}
                    </div>
                    <div className="truncate text-xs text-gray-500 dark:text-gray-400" title={`${formatDateAndTime(row.publishedAt)} · ${row.publishedByName ?? unknownPublisher}`}>
                      {formatDateAndTime(row.publishedAt)} · {row.publishedByName ?? unknownPublisher}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center">
                    <RowAction label={previewing ? labels.stopPreview : labels.preview} icon={previewing ? EyeSlashIcon : EyeIcon} active={previewing} onClick={() => togglePreview(row)} />
                    <RowAction label={labels.activate} icon={CheckCircleIcon} disabled={busy || row.isCurrent} onClick={() => setPending({ action: 'activate', row })} />
                    <RowAction
                      label={labels.restore}
                      icon={ArrowUturnLeftIcon}
                      disabled={busy}
                      onClick={() => (definition.hasUnpublishedChanges ? setPending({ action: 'restore', row }) : void run({ action: 'restore', row }))}
                    />
                    <RowAction label={row.isCurrent ? labels.activeCannotDelete : labels.delete} icon={TrashIcon} danger disabled={busy || row.isCurrent} onClick={() => setPending({ action: 'delete', row })} />
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <ConfirmDialog
        open={!!pending}
        onClose={() => setPending(null)}
        onConfirm={async () => {
          if (pending) await run(pending);
        }}
        variant={confirmation?.variant ?? 'warning'}
        title={confirmation?.title}
        message={confirmation?.message}
        confirmLabel={confirmation?.confirmLabel}
      />
    </PanelSection>
  );
};

export const HistoryPanel = memo(HistoryPanel_);
